import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE } from "./motion";
import { useLang, LANGS } from "../i18n";
import { track } from "../lib/analytics";
import { mediaSrc } from "../lib/media";

const links = [
  { to: "/services", label: "Services" },
  { to: "/work", label: "Projects" },
  { to: "/insights", label: "YUXEL Intelligence" },
  { to: "/about", label: "About YUXEL" },
  { to: "/contact", label: "Contact" },
];

export const Wordmark = () => {
  const { href, ov } = useLang();
  const logo = mediaSrc(ov("image:brand_logo", "/brand/yuxel-logo-wordmark.png"));
  return (
    <Link
      to={href("/")}
      data-testid="nav-logo-home-link"
      className="flex items-center"
      aria-label="YUXEL — home"
    >
      {logo ? (
        <img src={logo} alt="YUXEL" className="nav-wordmark h-8 w-auto max-w-[150px] object-contain sm:h-9" />
      ) : (
        <span className="flex items-baseline gap-1.5">
          <span className="font-display text-xl font-bold tracking-[0.35em] text-white">YUXEL</span>
          <span className="h-1.5 w-1.5 translate-y-[-2px] rounded-[1px] bg-gold" />
        </span>
      )}
    </Link>
  );
};

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      role="group"
      aria-label="Language"
      className={`nav-lang-toggle flex items-center rounded-full border border-white/15 p-0.5 font-mono text-[11px] uppercase tracking-[0.15em] ${className}`}
    >
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-toggle-${l}`}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 transition-colors duration-300 ${
            lang === l ? "bg-gold text-ink" : "text-white/60 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const { t, href, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/az";
  const isPortfolioIndex = ["/work", "/case-studies", "/az/work", "/az/case-studies"].includes(location.pathname.replace(/\/$/, ""));
  const isProjectRoute = /^\/(work|case-studies)(\/|$)/.test(location.pathname);
  const navLabel = (label) => isProjectRoute && lang === "az" && label === "About YUXEL" ? "Haqqımızda" : t(label);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[120] focus:rounded-full focus:bg-gold focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-ink"
      >
        {t("Skip to content")}
      </a>
      <header
        className={`creative-nav fixed inset-x-0 top-0 z-[90] transition-all duration-500 ${isHome ? "home-nav-light" : ""} ${isPortfolioIndex ? "portfolio-nav-light" : ""} ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""} ${
          scrolled || open
            ? (isHome && !open ? "home-nav-scrolled border-b border-black/10" : "glass-nav border-b border-white/10")
            : "border-b border-transparent"
        }`}
      >
        <nav
          className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8"
          aria-label="Primary"
        >
          <Wordmark />

          <div className="hidden items-center gap-7 lg:flex xl:gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={href(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-desktop-link group relative whitespace-nowrap text-sm font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "is-active text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {navLabel(l.label)}
              </NavLink>
            ))}
            {!isHome && <Link
              to={href("/contact")}
              data-testid="nav-cta-consultation-button"
              onClick={() => track("cta_click", { cta_id: "nav-consultation", cta_location: "navbar" })}
              className="nav-primary-cta group flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-champagne"
            >
              {t("Discuss your project")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>}
            <LangToggle />
          </div>

          <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            type="button"
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("Close menu") : t("Open menu")}
            className="nav-menu-button flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[85] flex flex-col justify-between bg-ink/95 px-6 pb-10 pt-28 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 + i * 0.07, ease: EASE }}
                  className="border-b border-white/10"
                >
                  <NavLink
                    to={href(l.to)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-4 font-display text-4xl font-bold ${
                        isActive ? "text-gold" : "text-white"
                      }`
                    }
                  >
                    {navLabel(l.label)}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
            >
              {!isHome && <Link
                to={href("/contact")}
                data-testid="nav-mobile-cta-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-semibold text-ink"
              >
                {t("Discuss your project")} <ArrowUpRight className="h-4 w-4" />
              </Link>}
              <p className="mt-6 text-center font-mono text-xs uppercase tracking-[0.25em] text-mist">
                {t("REAL ESTATE · CONSTRUCTION · ENTERTAINMENT · HOSPITALITY")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
